/**
 * packages/ui-shell/src/components/LegalModal.ts
 * プライバシーポリシー、利用規約、お問い合わせ（X @KILROYSANWHERE）表示モーダル
 */
import { t, subscribeLocaleChange } from '../i18n/i18n';
import { UiState } from '../state/UiState';

export type LegalTabType = 'privacy' | 'terms' | 'contact';

export class LegalModal {
  private element: HTMLElement;
  private currentTab: LegalTabType = 'privacy';

  private tabPrivacyEl?: HTMLElement;
  private tabTermsEl?: HTMLElement;
  private tabContactEl?: HTMLElement;
  private contentPrivacyEl?: HTMLElement;
  private contentTermsEl?: HTMLElement;
  private contentContactEl?: HTMLElement;
  private titleEl?: HTMLElement;
  private closeBtnEl?: HTMLButtonElement;
  private closeFooterBtnEl?: HTMLButtonElement;

  constructor(private container: HTMLElement, private uiState: UiState) {
    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.id = 'modalLegal';

    this.render();
    this.bindEvents();

    subscribeLocaleChange(() => {
      this.updateTexts();
    });

    this.container.appendChild(this.element);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  show(initialTab: LegalTabType = 'privacy'): void {
    this.switchTab(initialTab);
    this.updateTexts();
    this.element.classList.add('show');
  }

  hide(): void {
    this.element.classList.remove('show');
  }

  private switchTab(tab: LegalTabType): void {
    this.currentTab = tab;
    const tabs: LegalTabType[] = ['privacy', 'terms', 'contact'];
    tabs.forEach((tName) => {
      const tabEl = this.element.querySelector(`#legalTabHead_${tName}`);
      const contentEl = this.element.querySelector(`#legalContent_${tName}`);
      if (tName === tab) {
        tabEl?.classList.add('active');
        contentEl?.classList.add('active');
        (contentEl as HTMLElement)?.style.setProperty('display', 'block');
      } else {
        tabEl?.classList.remove('active');
        contentEl?.classList.remove('active');
        (contentEl as HTMLElement)?.style.setProperty('display', 'none');
      }
    });

    const texts = t().legal;
    if (this.titleEl) {
      if (tab === 'privacy') this.titleEl.textContent = `🔒 ${texts.privacyTitle}`;
      else if (tab === 'terms') this.titleEl.textContent = `📜 ${texts.termsTitle}`;
      else this.titleEl.textContent = `✉️ ${texts.contactTitle}`;
    }
  }

  private render(): void {
    const texts = t().legal;

    this.element.innerHTML = `
      <div class="modal-card" style="max-width: 680px; width: 92%; max-height: 85vh; display: flex; flex-direction: column;">
        <div class="modal-header" style="flex-shrink: 0;">
          <h3 id="legalModalTitle" style="font-size: 16px; margin: 0; display: flex; align-items: center; gap: 8px;">
            🔒 ${texts.privacyTitle}
          </h3>
          <button class="modal-close" id="btnLegalClose" aria-label="Close">✕</button>
        </div>

        <!-- タブセレクター -->
        <div class="panel-tabs" style="flex-shrink: 0; background: #faf8f5;">
          <div class="panel-tab active" id="legalTabHead_privacy" data-tab="privacy" style="font-size: 13px; padding: 10px;">
            ${texts.privacyTitle}
          </div>
          <div class="panel-tab" id="legalTabHead_terms" data-tab="terms" style="font-size: 13px; padding: 10px;">
            ${texts.termsTitle}
          </div>
          <div class="panel-tab" id="legalTabHead_contact" data-tab="contact" style="font-size: 13px; padding: 10px;">
            ${texts.contactTitle}
          </div>
        </div>

        <div class="modal-body" style="overflow-y: auto; flex: 1; padding: 18px 22px; font-size: 13px; line-height: 1.7; color: var(--color-text-main);">
          <!-- 1. プライバシーポリシー -->
          <div id="legalContent_privacy" class="legal-tab-pane"></div>

          <!-- 2. 利用規約 -->
          <div id="legalContent_terms" class="legal-tab-pane" style="display: none;"></div>

          <!-- 3. お問い合わせ -->
          <div id="legalContent_contact" class="legal-tab-pane" style="display: none;"></div>
        </div>

        <div class="modal-footer" style="flex-shrink: 0; padding: 12px 20px; border-top: 1px solid var(--color-card-border); display: flex; justify-content: flex-end;">
          <button class="btn btn-secondary" id="btnLegalFooterClose" style="padding: 6px 18px; font-size: 13px;">
            ${texts.closeBtn}
          </button>
        </div>
      </div>
    `;

    this.titleEl = this.element.querySelector('#legalModalTitle') as HTMLElement;
    this.closeBtnEl = this.element.querySelector('#btnLegalClose') as HTMLButtonElement;
    this.closeFooterBtnEl = this.element.querySelector('#btnLegalFooterClose') as HTMLButtonElement;

    this.tabPrivacyEl = this.element.querySelector('#legalTabHead_privacy') as HTMLElement;
    this.tabTermsEl = this.element.querySelector('#legalTabHead_terms') as HTMLElement;
    this.tabContactEl = this.element.querySelector('#legalTabHead_contact') as HTMLElement;

    this.contentPrivacyEl = this.element.querySelector('#legalContent_privacy') as HTMLElement;
    this.contentTermsEl = this.element.querySelector('#legalContent_terms') as HTMLElement;
    this.contentContactEl = this.element.querySelector('#legalContent_contact') as HTMLElement;

    this.renderTabsContent();
  }

  private renderTabsContent(): void {
    const texts = t().legal;

    if (this.contentPrivacyEl) {
      this.contentPrivacyEl.innerHTML = `
        <div style="background: #e6f9ed; border-left: 4px solid #10b981; padding: 10px 14px; border-radius: 4px; margin-bottom: 14px; font-size: 12px; color: #065f46;">
          ${texts.privacyBanner || ''}
        </div>
        
        <h4 style="margin: 12px 0 6px; font-size: 14px;">${texts.privacySec1Title || '1. 広告配信（Google AdSense 等）'}</h4>
        <p style="margin: 0 0 10px; color: var(--color-text-muted);">
          ${texts.privacySec1Desc || ''}
        </p>

        <h4 style="margin: 12px 0 6px; font-size: 14px;">${texts.privacySec2Title || '2. 有料ライセンス決済（Gumroad）'}</h4>
        <p style="margin: 0 0 10px; color: var(--color-text-muted);">
          ${texts.privacySec2Desc || ''}
        </p>

        <div style="margin-top: 14px; padding-top: 10px; border-top: 1px dashed var(--color-card-border); text-align: right;">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style="color: var(--color-primary); font-size: 12px; text-decoration: underline; font-weight: 600;">
            ${texts.openFullPrivacy || '↗ プライバシーポリシー全文を新しいタブで開く'}
          </a>
        </div>
      `;
    }

    if (this.contentTermsEl) {
      this.contentTermsEl.innerHTML = `
        <h4 style="margin: 0 0 6px; font-size: 14px;">${texts.termsSec1Title || '1. ライセンスとエディション'}</h4>
        <p style="margin: 0 0 10px; color: var(--color-text-muted);">
          ${texts.termsSec1Desc || ''}
        </p>

        <h4 style="margin: 12px 0 6px; font-size: 14px;">${texts.termsSec2Title || '2. 著作権およびコンテンツ責任'}</h4>
        <p style="margin: 0 0 10px; color: var(--color-text-muted);">
          ${texts.termsSec2Desc || ''}
        </p>

        <h4 style="margin: 12px 0 6px; font-size: 14px;">${texts.termsSec3Title || '3. 禁止事項'}</h4>
        <p style="margin: 0 0 10px; color: var(--color-text-muted);">
          ${texts.termsSec3Desc || ''}
        </p>

        <div style="margin-top: 14px; padding-top: 10px; border-top: 1px dashed var(--color-card-border); text-align: right;">
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" style="color: var(--color-primary); font-size: 12px; text-decoration: underline; font-weight: 600;">
            ${texts.openFullTerms || '↗ 利用規約全文を新しいタブで開く'}
          </a>
        </div>
      `;
    }

    if (this.contentContactEl) {
      this.contentContactEl.innerHTML = `
        <div style="text-align: center; padding: 16px 0;">
          <div style="font-size: 36px; margin-bottom: 10px;">📮</div>
          <h4 style="margin: 0 0 8px; font-size: 15px; font-weight: 700;">${texts.contactTitle}</h4>
          <p style="font-size: 12px; color: var(--color-text-muted); max-width: 440px; margin: 0 auto 18px;">
            ${texts.contactDesc}
          </p>

          <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 24px; font-size: 14px; font-weight: 700; text-decoration: none;">
            <span style="font-size: 16px;">𝕏</span> <span>${texts.contactButton || '@KILROYSANWHERE に問い合わせる'}</span>
          </a>

          <div style="margin-top: 18px; font-size: 12px; color: var(--color-text-muted);">
            ${texts.operatorLabel || '運営者: <strong>KILROYSAN</strong>'}<br>
            公式サイト: <a href="https://jigglytouch.com" style="color: var(--color-primary); text-decoration: underline;">https://jigglytouch.com</a>
          </div>
        </div>
      `;
    }
  }

  private bindEvents(): void {
    this.closeBtnEl?.addEventListener('click', () => this.hide());
    this.closeFooterBtnEl?.addEventListener('click', () => this.hide());

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.hide();
      }
    });

    this.tabPrivacyEl?.addEventListener('click', () => this.switchTab('privacy'));
    this.tabTermsEl?.addEventListener('click', () => this.switchTab('terms'));
    this.tabContactEl?.addEventListener('click', () => this.switchTab('contact'));

    // 画面全体の /privacy.html および /terms.html リンクをインターセプトしてアプリ内モーダルで表示
    // ※ただし、target="_blank" のリンクや、モーダル内のリンクはそのまま新しいタブで開く
    document.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement)?.closest('a');
      if (!target) return;

      // target="_blank" のリンクやモーダル内部のリンクはインターセプトせず、ブラウザの既定動作（新しいタブ）に任せる
      if (target.getAttribute('target') === '_blank' || target.closest('#modalLegal')) {
        return;
      }

      const href = target.getAttribute('href');
      if (href === '/privacy.html') {
        if ((e as MouseEvent).button === 0 && !(e as MouseEvent).ctrlKey && !(e as MouseEvent).metaKey) {
          e.preventDefault();
          this.show('privacy');
        }
      } else if (href === '/terms.html') {
        if ((e as MouseEvent).button === 0 && !(e as MouseEvent).ctrlKey && !(e as MouseEvent).metaKey) {
          e.preventDefault();
          this.show('terms');
        }
      }
    });
  }

  private updateTexts(): void {
    const texts = t().legal;
    if (this.tabPrivacyEl) this.tabPrivacyEl.textContent = texts.privacyTitle;
    if (this.tabTermsEl) this.tabTermsEl.textContent = texts.termsTitle;
    if (this.tabContactEl) this.tabContactEl.textContent = texts.contactTitle;
    if (this.closeFooterBtnEl) this.closeFooterBtnEl.textContent = texts.closeBtn;
    this.renderTabsContent();
    this.switchTab(this.currentTab);
  }
}
